import { useCallback, useEffect, useMemo, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import MessageInput from 'supportAssistant/components/Chat/MessageInput'
import { isChip } from 'supportAssistant/utils/actionsUtils'
import Action from 'supportAssistant/components/Chat/MessageMeta/Action'
import BodyText from 'LuxKit/Typography/BodyText'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { useAppSelector } from 'hooks/reduxHooks'
import { getLuxPlusSupportContactNumber } from 'selectors/supportContactSelector'
import { selectLoggedIn } from 'selectors/accountSelectors'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import useInfiniteConciergeDetails from 'loyalty/hooks/useInfiniteConciergeDetails'
import { Experiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import { trackClientEvent } from 'analytics/analytics'
import LesaPredictedWelcomeChips from 'supportAssistant/components/Chat/LesaPredictedWelcomeChips'

const CenterContainer = styled(Group)`
  flex: 1;
  padding: ${rem(16)};
`

const InputContainer = styled.div`
  width: 100%;
  max-width: ${rem(720)};
`

const ActionsContainer = styled.div`
  width: 100%;
  max-width: ${rem(720)};
`

interface Props {
  isTyping: boolean
  actions?: Array<App.ChatMessageMetaContent>
  payload?: App.ChatMessageMetaPayload
  className?: string
}

function ChipRow({
  chips,
  payload,
  onClick,
}: {
  chips: Array<App.ChatMessageMetaContent>
  payload?: App.ChatMessageMetaPayload
  onClick: () => void
}) {
  if (chips.length === 0) return null
  return (
    <ActionsContainer>
      <Group
        direction="horizontal"
        tabletDirection="horizontal"
        wrap="wrap"
        horizontalAlign="center"
        verticalAlign="center"
        gap={8}
        fullWidth
      >
        {chips.map((action, index) => (
          <div key={`${action.text}-${index}`} onClick={onClick}>
            <Action
              action={action}
              payload={payload}
            />
          </div>
        ))}
      </Group>
    </ActionsContainer>
  )
}

function ChatEmptyState(props: Props) {
  const { isTyping, actions, payload, className } = props
  const intl = useIntl()
  const welcome = useAppSelector(state => state.supportAssistant.welcomeMessage.message)
  const isLoggedIn = useAppSelector(selectLoggedIn)

  const chipActions = useMemo(() => {
    const existing = actions?.filter(isChip) ?? []
    return existing.length ? existing : (welcome?.meta?.content ?? []).filter(isChip)
  }, [actions, welcome?.meta?.content])
  const actionPayload = { ...payload, ...welcome?.meta?.payload }
  const excludedActionKeys = useMemo(
    () => chipActions.flatMap(chip => chip.action?.key ? [chip.action.key] : []),
    [chipActions],
  )

  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const luxPlusSupportContactNumber = useAppSelector((state) => getLuxPlusSupportContactNumber(state).numberHumanReadable)

  // Société Infinite takes precedence: show the member's concierge number regardless of LuxPlus.
  const isInfiniteMember = useAppSelector(getLoyaltyIsInfiniteAccountTier)
  const concierge = useInfiniteConciergeDetails()

  const { isAnyVariant: isEmptyChatFreeTextHidden } = useExperiment(Experiments.opexLESAEmptyChatFreeText)

  const hasTrackedCohort = useRef(false)
  useEffect(() => {
    if (!hasTrackedCohort.current) {
      hasTrackedCohort.current = true
      trackClientEvent({
        subject: 'support_assistant',
        action: isEmptyChatFreeTextHidden
          ? 'lesa-empty-chat-no-free-text'
          : 'lesa-empty-chat-with-free-text',
        category: 'logging',
        type: 'operational',
      })
    }
  }, [isEmptyChatFreeTextHidden])

  const handlePillClick = useCallback(() => {
    trackClientEvent({
      subject: 'support_assistant',
      action: 'lesa-empty-chat-pill-clicked',
      category: 'logging',
      type: 'interaction',
      optimizelyEventId: '6310489135775744',
      optimizelyEventKey: 'lesa_empty_chat_pill_clicked',
    })
  }, [])

  return (
    <CenterContainer className={className} direction="vertical" horizontalAlign="center" verticalAlign="center" gap={16} fullWidth>
      <HeadingTextBlock variant="heading4" align="center">
        {intl.formatMessage({
          id: 'SEWHldBBDxE8nRNbCzf0',
          defaultMessage: 'How can we help you?',
          description: 'Heading shown on empty support chat state in the support area',
        })}
      </HeadingTextBlock>
      {isInfiniteMember && concierge ? (
        <BodyText variant="medium" align="center" colour="light-foreground-alt">
          <FormattedMessage
            id="uQazy7CAfSv7Eep0vNra"
            defaultMessage="You can skip the queue by calling your Infinite Concierge representative on {phoneNumber}"
            description="Message for Société Infinite members showing their concierge representative phone number"
            values={{ phoneNumber: concierge.phoneNumber }}
          />
        </BodyText>
      ) : canViewLuxPlusBenefits && luxPlusSupportContactNumber ? (
        <BodyText variant="medium" align="center" colour="light-foreground-alt">
          <FormattedMessage
            id="rdLJQ7vnaHehISaoqOun"
            defaultMessage="You can skip the queue by calling LuxPlus+ Priority Support on {phoneNumber}"
            description="Message for LuxPlus members showing priority support phone number"
            values={{ phoneNumber: luxPlusSupportContactNumber }}
          />
        </BodyText>
      ) : null}
      {!isEmptyChatFreeTextHidden && (
        <InputContainer>
          <MessageInput isTyping={isTyping} showDisclaimer={false} />
        </InputContainer>
      )}
      <Group direction="vertical" gap={48} horizontalAlign="center" fullWidth>
        <ChipRow chips={chipActions} payload={actionPayload} onClick={handlePillClick} />
        {isLoggedIn && (
          <LesaPredictedWelcomeChips
            payload={actionPayload}
            excludedActionKeys={excludedActionKeys}
            onClick={handlePillClick}
          />
        )}
      </Group>
    </CenterContainer>
  )
}

export default ChatEmptyState
